import React from 'react';
import { BlockReleaseRequestContextType } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext.types';
import { useGetCombinedReleases } from '@components/BlockRelease/utils/hooks/useGetCombinedReleases/useGetCombinedReleases';
import { useGetBlockInfo } from '@components/BlockRelease/utils/hooks/useGetBlockInfo/useGetBlockInfo';
import { useGetSelectedReleaseInfo } from '@components/BlockRelease/utils/hooks/useGetSelectedReleaseInfo/useGetSelectedReleaseInfo';
import { BlockReleaseApiResponse } from '@data/blockReleases/types/BlockRelease.types';
import { DEFAULT_SELECTION_ALL } from '@utilities/constants';
import { useSetBlockAccessor } from '@components/BlockRelease/utils/hooks/useSetBlockAccessor/useSetBlockAccessor';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';
import { UserProfileDetailsSelector } from '@store/selectors/UserProfileSelector';
import { useSelector } from 'react-redux';
import { sortBlockReleases } from '@components/BlockRelease/utils/sortBlockReleases/sortBlockReleases';
import {
	formatRoundAndParseReleaseDateTime
} from '@components/BlockRelease/utils/roundAndParseReleaseDateTime/roundAndParseReleaseDateTime';
import {
	combineFlipReleases
} from '@components/BlockRelease/utils/releaseFlipCombiner/combineFlipReleases';

const BlockReleaseRequestContext = React.createContext<BlockReleaseRequestContextType | undefined>(
	undefined,
);

export const useBlockReleaseRequestContext = () => {
	const ctx = React.useContext(BlockReleaseRequestContext);

	if (!ctx) {
		throw new Error(
			'useBlockReleaseRequestContext must be used in a child of BlockReleaseRequestProvider.',
		);
	}

	return ctx;
};

const roundCombinedReleases = (combinedReleases: BlockReleaseApiResponse[]): BlockReleaseApiResponse[] => {
	return combinedReleases.map((release) => {
		return {
			...release,
			startDateTime: formatRoundAndParseReleaseDateTime(release.startDateTime),
			endDateTime: formatRoundAndParseReleaseDateTime(release.endDateTime),
		};
	});
};

export const BlockReleaseRequestProvider: React.FC = ({ children }) => {
	const { schedules, setSchedules, getBlockInfo } = useGetBlockInfo();
	const { combinedReleases, setCombinedReleases } = useGetCombinedReleases({ schedules, getBlockInfo });
	const roundedCombinedReleases = React.useMemo(() => roundCombinedReleases(combinedReleases), [combinedReleases]);
	const sortedReleases = React.useMemo(() => sortBlockReleases(roundedCombinedReleases), [roundedCombinedReleases]);
	const combinedFlipReleases = React.useMemo(() => combineFlipReleases(sortedReleases), [sortedReleases]);

	const [selectedSurgeonFilter, setSelectedSurgeonFilter] = React.useState<string>(DEFAULT_SELECTION_ALL);
	const prevSelectedRelease = React.useRef<BlockReleaseApiResponse>();

	const { selectedReleaseInfo } = useGetSelectedReleaseInfo({
		schedules,
		releases: combinedFlipReleases,
		selectedSurgeonFilter,
		prevSelectedRelease,
	});
	const { selectedRelease } = selectedReleaseInfo;

	const { blockAccessor, setBlockReleaseRequestAccessor } = useSetBlockAccessor(selectedRelease);
	const { userId } = useSelector(UserProfileDetailsSelector);
	const blockInUse = React.useMemo(
		() => blockAccessor.length > 0 && blockAccessor[0].userId !== userId,
		[blockAccessor, userId],
	);

	React.useEffect(() => {
		if (selectedRelease && prevSelectedRelease.current !== selectedRelease) {
			setSchedules([]);
			if (selectedRelease.state === BlockReleaseState.RECOMMENDED_RELEASE ||
			selectedRelease.state === BlockReleaseState.FLIP_RECOMMENDATION) {
				setBlockReleaseRequestAccessor();
			}

			prevSelectedRelease.current = selectedRelease;
			getBlockInfo(
				selectedRelease.npis[0],
				selectedRelease.unit,
				selectedRelease.startDateTime,
			);
		}
	}, [
		selectedRelease,
		setSchedules,
		getBlockInfo,
		setBlockReleaseRequestAccessor,
		blockAccessor,
		userId,
	]);

	return (
		<BlockReleaseRequestContext.Provider
			value={{
				selectedReleaseInfo,
				releases: combinedFlipReleases,
				setCombinedReleases,
				selectedSurgeonFilter,
				setSelectedSurgeonFilter,
				blockAccessor,
				blockInUse,
			}}
		>
			{children}
		</BlockReleaseRequestContext.Provider>
	);
};
